import { FileQuestion, Lock, ServerCrash, ShieldAlert } from 'lucide-react';
import { Link, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/domains/identity/AuthProvider';
import { Button } from '@/shared/ui';
import { buttonClasses } from '@/shared/ui/Button';
import { routes } from '../routes';

function Shell({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 rounded-full bg-surface-hover p-4 text-fg-subtle">{icon}</div>
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="mt-2 max-w-md text-base text-fg-muted">{body}</p>
      <div className="mt-6 flex items-center gap-2">
        {action}
        <Link to={routes.overview} className={buttonClasses('secondary')}>
          {t('errors.goHome')}
        </Link>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <Shell
      icon={<FileQuestion className="h-6 w-6" />}
      title={t('errors.notFoundTitle')}
      body={t('errors.notFoundBody')}
    />
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation();
  return (
    <Shell
      icon={<ShieldAlert className="h-6 w-6" />}
      title={t('errors.forbiddenTitle')}
      body={t('errors.forbiddenBody')}
    />
  );
}

/** Router-level boundary: an uncaught render error must not leave a blank page. */
export function RouteErrorPage() {
  const { t } = useTranslation();
  const error = useRouteError();
  // Surfaced in the browser console for a developer; the user gets the friendly page.
  if (import.meta.env.DEV) console.error('Route error:', error);

  return (
    <Shell
      icon={<ServerCrash className="h-6 w-6" />}
      title={t('errors.serverTitle')}
      body={t('errors.serverBody')}
      action={
        <Button variant="primary" onClick={() => window.location.reload()}>
          {t('common.refresh')}
        </Button>
      }
    />
  );
}

/**
 * The locked-feature page. Not a redirect: the Blueprint requires that a user who lacks a
 * feature can see that it exists and ask for it.
 */
export function FeatureLockedPage({ featureKey }: { featureKey: string }) {
  const { t } = useTranslation();
  const { featureName, user } = useAuth();
  const name = featureName(featureKey);

  const prefill = t('feature.requestPrefill', { name, email: user?.email ?? '' });

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 rounded-full bg-surface-hover p-4 text-fg-subtle">
        <Lock className="h-6 w-6" />
      </div>
      <h1 className="text-lg font-semibold">{t('feature.lockedTitle', { name })}</h1>
      <p className="mt-2 max-w-md text-base text-fg-muted">{t('feature.lockedBody')}</p>
      <div className="mt-6">
        <Link
          to={`${routes.support}?type=other&prefill=${encodeURIComponent(prefill)}`}
          className={buttonClasses('primary')}
        >
          {t('feature.requestAccess')}
        </Link>
      </div>
    </div>
  );
}
